/**
 * Generic document caching service using IndexedDB.
 * Stores document collections per group (e.g. quotes, photos)
 * for instant offline-first display with stale-while-revalidate revalidation.
 */

const DB_NAME = "kekkle-document-cache";
const DB_VERSION = 1;
const STORE_NAME = "documents";

export type CachedDocumentEntry<T> = {
  key: string;
  collection: string;
  groupId: string;
  documents: T[];
  updatedAt: number;
  /**
   * Delta-sync cursor (epoch ms of the server `updated_at` to resume from).
   * Null/absent means the documents were never fully synced from the server.
   */
  cursor?: number | null;
};

/** Documents plus the delta-sync cursor they were synced up to. */
export type CachedSyncState<T> = {
  documents: T[];
  cursor: number | null;
};

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Check whether IndexedDB is available in the current environment
 */
function isIndexedDBSupported(): boolean {
  return typeof window !== "undefined" && typeof indexedDB !== "undefined";
}

/**
 * Initialize or get the IndexedDB connection for the document cache
 */
function getDB(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }

  if (!isIndexedDBSupported()) {
    return Promise.reject(
      new Error("IndexedDB is not supported in this environment"),
    );
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };

    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, {
          keyPath: "key",
        });
        objectStore.createIndex("collection", "collection", { unique: false });
        objectStore.createIndex("groupId", "groupId", { unique: false });
      }
    };
  });

  return dbPromise;
}

/**
 * Generate a cache key for a collection within a group
 * @param collection - Collection name (e.g., "quotes", "photos")
 * @param groupId - Group ID
 * @returns Cache key formatted as "collection:groupId"
 */
export function generateDocumentKey(
  collection: string,
  groupId: string,
): string {
  return `${collection}:${groupId}`;
}

// Standard ISO 8601 date string pattern (e.g. "2026-09-17T15:26:00.000Z")
const ISO_DATE_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * Recursively revives ISO date strings and Timestamp objects into Date objects
 * across any document without requiring any hardcoded field names.
 */
function reviveDates<T>(item: T): T {
  if (!item || typeof item !== "object") return item;
  if (item instanceof Date) return item;

  const target = item as Record<string, unknown>;

  for (const [key, val] of Object.entries(target)) {
    if (typeof val === "string" && ISO_DATE_REGEX.test(val)) {
      const parsed = new Date(val);
      if (!isNaN(parsed.getTime())) {
        target[key] = parsed;
      }
    } else if (val && typeof val === "object") {
      if (
        "toDate" in val &&
        typeof (val as { toDate?: unknown }).toDate === "function"
      ) {
        target[key] = (val as { toDate: () => Date }).toDate();
      } else {
        reviveDates(val);
      }
    }
  }

  return item;
}

/**
 * Retrieve cached documents for a collection and group
 * @param collection - Collection name (e.g. "quotes", "photos")
 * @param groupId - Group ID
 * @returns Array of documents or null if not in cache
 */
export async function getCachedDocuments<T>(
  collection: string,
  groupId: string,
): Promise<T[] | null> {
  const state = await getCachedSyncState<T>(collection, groupId);
  return state?.documents ?? null;
}

/**
 * Retrieve cached documents together with their delta-sync cursor
 * @param collection - Collection name (e.g. "quotes", "photos")
 * @param groupId - Group ID
 * @returns Documents and cursor, or null if not in cache
 */
export async function getCachedSyncState<T>(
  collection: string,
  groupId: string,
): Promise<CachedSyncState<T> | null> {
  if (!isIndexedDBSupported() || !groupId) return null;

  try {
    const db = await getDB();
    const key = generateDocumentKey(collection, groupId);
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const result = request.result as CachedDocumentEntry<T> | undefined;
        if (!result || !Array.isArray(result.documents)) {
          resolve(null);
          return;
        }

        const revivedDocs = result.documents.map((doc) => reviveDates(doc));
        resolve({ documents: revivedDocs, cursor: result.cursor ?? null });
      };

      request.onerror = () => {
        console.error(
          `Error reading cached documents for ${key}:`,
          request.error,
        );
        resolve(null);
      };
    });
  } catch (error) {
    console.error(
      `Error accessing document cache for ${collection}:${groupId}:`,
      error,
    );
    return null;
  }
}

/**
 * Store documents in the cache for a collection and group
 * @param collection - Collection name (e.g. "quotes", "photos")
 * @param groupId - Group ID
 * @param documents - Array of document objects to cache
 * @param cursor - Delta-sync cursor the documents are synced up to (null if not delta-synced)
 */
export async function setCachedDocuments<T>(
  collection: string,
  groupId: string,
  documents: T[],
  cursor: number | null = null,
): Promise<void> {
  if (!isIndexedDBSupported() || !groupId) return;

  try {
    const db = await getDB();
    const key = generateDocumentKey(collection, groupId);
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const entry: CachedDocumentEntry<T> = {
      key,
      collection,
      groupId,
      documents,
      updatedAt: Date.now(),
      cursor,
    };

    store.put(entry);

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error(
      `Error writing document cache for ${collection}:${groupId}:`,
      error,
    );
  }
}

/**
 * Retrieve a single document by its _id from the group's cached collection
 * @param collection - Collection name (e.g. "quotes", "photos")
 * @param groupId - Group ID
 * @param docId - Document ID (_id)
 * @returns The document or null if not found
 */
export async function getCachedDocumentById<T extends { _id: string }>(
  collection: string,
  groupId: string,
  docId: string,
): Promise<T | null> {
  const docs = await getCachedDocuments<T>(collection, groupId);
  if (!docs) return null;
  return docs.find((d) => d._id === docId) ?? null;
}

/**
 * Clear cached documents matching a collection and/or groupId
 * @param collection - Optional collection filter
 * @param groupId - Optional groupId filter
 */
export async function clearCachedDocuments(
  collection?: string,
  groupId?: string,
): Promise<void> {
  if (!isIndexedDBSupported()) return;

  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    if (collection && groupId) {
      store.delete(generateDocumentKey(collection, groupId));
      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    }

    const request = store.getAllKeys();
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const keys = request.result as string[];
        const targetPattern = collection ? `${collection}:` : "";

        keys.forEach((key) => {
          if (!targetPattern || key.startsWith(targetPattern)) {
            if (!groupId || key.endsWith(`:${groupId}`)) {
              store.delete(key);
            }
          }
        });

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      };

      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Error clearing cached documents:", error);
  }
}

/**
 * Clear all cached documents across all collections and groups (used on logout)
 */
export async function clearAllCachedDocuments(): Promise<void> {
  if (!isIndexedDBSupported()) return;

  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    store.clear();

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error("Error clearing all cached documents:", error);
  }
}
